import { useState, useEffect, useRef, useCallback } from 'react'
import { useWebSocketContext } from '../context/WebSocketContext'
import { playAudio as managerPlayAudio, stopCurrent, subscribe } from '../utils/creoleAudioManager'

/**
 * Common hook for dictionary pronunciation audio (Pattern 1).
 * Replaces ~60 lines of duplicated WebSocket+polling code across learn pages.
 *
 * @param {Object} options
 * @param {string} options.channel - WebSocket channel name (e.g. 'learner-flashcard-pronunciation')
 * @param {function} [options.onInsufficientCredits] - Called with (required, available) on 402
 * @returns {{ handlePlayAudio: function, playingId: number|null, stopAudio: function }}
 */
export default function useCreoleAudio({ channel, onInsufficientCredits }) {
  const { addListener } = useWebSocketContext()
  const [playingId, setPlayingId] = useState(null)
  const playingIdRef = useRef(null)
  const pendingPronunciationRef = useRef(new Map())

  // Subscribe to audio manager for cross-component stop
  useEffect(() => {
    return subscribe((activeId) => {
      // If another audio source started playing, clear our state
      if (activeId !== playingIdRef.current && playingIdRef.current !== null) {
        pendingPronunciationRef.current.clear()
        playingIdRef.current = null
        setPlayingId(null)
      }
    })
  }, [])

  // WebSocket listener for pronunciation ready events
  useEffect(() => {
    if (!addListener) return
    const removeListener = addListener(channel, (data) => {
      if (data.event === 'dictionary_pronunciation_ready' && data.entry_id && data.audio_url) {
        const wsEntryId = Number(data.entry_id)
        const pending = pendingPronunciationRef.current.get(wsEntryId)
        if (pending) {
          pending.resolve(data.audio_url)
          pendingPronunciationRef.current.delete(wsEntryId)
        }
      }
    })
    return removeListener
  }, [addListener, channel])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pendingPronunciationRef.current.forEach(({ reject }) => {
        reject(new Error('Component unmounted'))
      })
      pendingPronunciationRef.current.clear()
      if (playingIdRef.current !== null) {
        stopCurrent()
      }
    }
  }, [])

  /**
   * Play pronunciation audio for a dictionary entry.
   *
   * @param {number} entryId - Dictionary entry ID
   * @param {Object} [options]
   * @param {string} [options.pronunciationText] - Custom text for TTS (phrase cards, fill-blank)
   * @param {string} [options.cachedAudioUrl] - Pre-cached audio URL (alphabet letters)
   */
  const handlePlayAudio = useCallback(async (entryId, options = {}) => {
    if (!entryId) return

    // If tapping the same entry, toggle off
    if (playingIdRef.current === entryId) {
      stopCurrent()
      playingIdRef.current = null
      setPlayingId(null)
      return
    }

    setPlayingId(entryId)
    playingIdRef.current = entryId

    // If a cached audio URL is provided, play it directly
    if (options.cachedAudioUrl) {
      try {
        await managerPlayAudio(entryId, options.cachedAudioUrl)
      } catch { /* ignore */ }
      if (playingIdRef.current === entryId) {
        setPlayingId(null)
        playingIdRef.current = null
      }
      return
    }

    try {
      const api = (await import('../services/api')).default
      const body = options.pronunciationText ? { pronunciation_text: options.pronunciationText } : {}
      const res = await api.post(`/v1/dictionaries/public/${entryId}/generate-pronunciation/`, body)

      if (res.data.audio_url) {
        if (playingIdRef.current !== entryId) return
        await managerPlayAudio(entryId, res.data.audio_url)
        if (playingIdRef.current === entryId) {
          setPlayingId(null)
          playingIdRef.current = null
        }
        return
      }

      if (res.data.status === 'pending' || res.status === 202) {
        const wsPromise = new Promise((resolve, reject) => {
          pendingPronunciationRef.current.set(entryId, { resolve, reject })
          setTimeout(() => {
            if (pendingPronunciationRef.current.has(entryId)) {
              pendingPronunciationRef.current.delete(entryId)
              reject(new Error('Timeout waiting for pronunciation'))
            }
          }, 90000)
        })

        const pollPromise = (async () => {
          for (let attempt = 0; attempt < 3; attempt++) {
            await new Promise(resolve => setTimeout(resolve, 30000))
            if (playingIdRef.current !== entryId) return null
            if (!pendingPronunciationRef.current.has(entryId)) return null
            try {
              const checkResult = await api.get(`/v1/dictionaries/public/${entryId}/check-audio/`)
              if (checkResult.data.status === 'ready' && checkResult.data.audio_url) {
                pendingPronunciationRef.current.delete(entryId)
                return checkResult.data.audio_url
              }
            } catch (e) { /* continue polling */ }
          }
          return null
        })()

        try {
          const audioUrl = await Promise.race([wsPromise, pollPromise])
          if (audioUrl) {
            if (playingIdRef.current !== entryId) return
            await managerPlayAudio(entryId, audioUrl)
            if (playingIdRef.current === entryId) {
              setPlayingId(null)
              playingIdRef.current = null
            }
            return
          }
        } catch {
          // WebSocket timeout rejected
        }
      }
    } catch (err) {
      if (err.response?.status === 402 && onInsufficientCredits) {
        onInsufficientCredits(err.response.data?.required || 0, err.response.data?.available || 0)
      } else {
        console.error('Failed to play audio:', err)
      }
    }
    pendingPronunciationRef.current.delete(entryId)
    setPlayingId(null)
    playingIdRef.current = null
  }, [onInsufficientCredits])

  const stopAudio = useCallback(() => {
    stopCurrent()
    pendingPronunciationRef.current.clear()
    playingIdRef.current = null
    setPlayingId(null)
  }, [])

  return { handlePlayAudio, playingId, stopAudio }
}
